import React, { useEffect } from 'react';
import { X, Keyboard } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const isMac = navigator.platform.toUpperCase().includes('MAC');
const mod = isMac ? '⌘' : 'Ctrl';

const Key = ({ label }) => (
  <kbd className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-1.5 text-xs font-mono text-foreground/80 bg-accent border border-input rounded-md shadow-sm">
    {label}
  </kbd>
);

const KeyboardShortcutsModal = ({ onClose }) => {
  const { t } = useTranslation();

  const SHORTCUTS = [
    {
      section: t('keyboard_shortcuts.navigation'),
      items: [
        { keys: [`${mod}K`],        desc: t('nav.create.new') },
        { keys: [`${mod}I`],        desc: t('bots.chat.new_chat') },
        { keys: [`${mod}/`],        desc: t('keyboard_shortcuts.shortcuts.global_search') },
        { keys: [`${mod}\\`],       desc: t('keyboard_shortcuts.shortcuts.toggle_sidebar') },
        { keys: ['G', 'H'],         desc: t('keyboard_shortcuts.shortcuts.go_home') },
        { keys: ['G', 'T'],         desc: t('keyboard_shortcuts.shortcuts.go_tasks') },
        { keys: ['G', 'P'],         desc: t('keyboard_shortcuts.shortcuts.go_planner') },
        { keys: ['G', 'D'],         desc: t('keyboard_shortcuts.shortcuts.go_documents') },
      ],
    },
    {
      section: t('keyboard_shortcuts.editing'),
      items: [
        { keys: [`${mod}Z`],        desc: t('keyboard_shortcuts.shortcuts.undo') },
        { keys: ['Enter'],           desc: t('keyboard_shortcuts.shortcuts.confirm') },
        { keys: ['Esc'],             desc: t('keyboard_shortcuts.shortcuts.close_modal') },
      ],
    },
    {
      section: t('keyboard_shortcuts.task_lists'),
      items: [
        { keys: [t('ui_common.shortcuts.click_row')], desc: t('keyboard_shortcuts.shortcuts.open_side_panel') },
        { keys: ['!p1-!p4'],        desc: t('keyboard_shortcuts.shortcuts.set_priority') },
        { keys: ['@today'],         desc: t('keyboard_shortcuts.shortcuts.set_due_date') },
        { keys: ['~30m'],           desc: t('keyboard_shortcuts.shortcuts.set_duration') },
      ],
    },
    {
      section: t('keyboard_shortcuts.help_section'),
      items: [
        { keys: ['?'],               desc: t('keyboard_shortcuts.shortcuts.show_shortcuts') },
        { keys: ['F1'],              desc: t('keyboard_shortcuts.shortcuts.go_help') },
      ],
    },
  ];

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        // BG_38: constrain the card to the viewport and scroll the shortcut
        // list so the card is never cropped top/bottom on short windows.
        className="bg-popover border border-border rounded-2xl shadow-2xl w-full max-w-md max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2 text-foreground font-semibold">
            <Keyboard className="w-4 h-4 text-muted-foreground" />
            {t('keyboard_shortcuts.title')}
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Shortcuts */}
        <div className="p-5 space-y-5 overflow-y-auto flex-1 min-h-0">
          {SHORTCUTS.map(({ section, items }) => (
            <div key={section}>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">{section}</p>
              <div className="space-y-2">
                {items.map(({ keys, desc }) => (
                  <div key={desc} className="flex items-center justify-between gap-4">
                    <span className="text-sm text-foreground/80">{desc}</span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {keys.map((k, i) => (
                        <Key key={i} label={k} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="px-5 pb-4 text-xs text-muted-foreground/70 text-center">
          {t('keyboard_shortcuts.press_to_close')} <Key label="?" /> {t('keyboard_shortcuts.or_esc')}
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcutsModal;
